// Adapts the new graphql-gateway response shape to the shape expected by
// web-shared-components portfolio templates (Adams / Moodie / Newton),
// which were built against the legacy portfolios-api schema:
//   Photo.name           -> now Photo.title
//   Photo.legacyId       -> removed (derived from the global id when possible)
//   Photo.images(sizes)  -> now Photo.urls { size_600 size_1024 size_2048 }
//   PhotoConnection.totalCount -> removed (derived from edges length)

// Legacy 500px resize-size codes requested by the templates, mapped to the
// closest of the sized urls the new gateway exposes. The templates read
// images[0], so the first key decides what actually loads.
const PHOTO_SIZE_URL_KEYS = {
  32: 'size_1024',
  33: 'size_1024',
  34: 'size_1024',
  35: 'size_2048',
  36: 'size_2048',
};

// Adams / Newton render photos as a full-viewport slideshow at >= 768px
// (isTabletPortrait in web-shared-components), so they need size_2048 there.
const SLIDESHOW_PHOTO_SIZE_URL_KEYS = {
  32: 'size_2048',
  33: 'size_2048',
  34: 'size_2048',
  35: 'size_2048',
  36: 'size_2048',
};

const COVER_SIZE_URL_KEYS = {
  21: 'size_1024',
  35: 'size_2048',
};

// Adams shows folder covers as a full-screen home carousel, so it wants 2K.
const ADAMS_COVER_SIZE_URL_KEYS = {
  21: 'size_2048',
  35: 'size_2048',
};

// Same breakpoint the templates use to switch between the slideshow layout
// and the masonry / list layouts. Evaluated once per data load.
const isSlideshowViewport = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(min-width: 768px)').matches;

const decodeLegacyId = (globalId) => {
  if (!globalId) return globalId;
  try {
    const decoded = window.atob(globalId);
    const parts = decoded.split(':');
    return parts[parts.length - 1] || globalId;
  } catch (e) {
    return globalId;
  }
};

const toImages = (urls, sizeUrlKeys) => {
  if (!urls) return [];
  return Object.keys(sizeUrlKeys)
    .map((size) => {
      const url = urls[sizeUrlKeys[size]];
      return {
        size: Number(size),
        url,
        jpegUrl: url,
        webpUrl: url,
      };
    })
    .filter(image => image.url);
};

const adaptPhoto = (photo, sizeUrlKeys) => {
  if (!photo) return photo;
  return {
    ...photo,
    legacyId: decodeLegacyId(photo.id),
    name: photo.title,
    images: toImages(photo.urls, sizeUrlKeys),
  };
};

// The templates render the cover full-screen, so images[0] stays 2K. Share
// crawlers (og:image / twitter:image) want ~1200px instead, so the meta url is
// resolved separately and exposed as coverPhoto.metaImageUrl (see Portfolio.js).
const META_IMAGE_SIZE = 1024;

const pickMetaImageUrl = (images) => {
  if (!images || images.length === 0) return undefined;
  const meta = images.find(image => image?.size === META_IMAGE_SIZE);
  return (meta || images[0])?.url;
};

const adaptCoverPhoto = (coverPhoto) => {
  if (!coverPhoto) return coverPhoto;
  // PortfolioCoverPhoto variant: getCoverPhotoUrl in the templates only knows
  // coverPhoto.avatar.images / coverPhoto.images, so the photo urls must also
  // be exposed as a top-level images array
  if (coverPhoto.photo) {
    const { urls } = coverPhoto.photo;
    const images = urls ? [{ url: urls.size_2048 }] : [];
    return {
      ...coverPhoto,
      images,
      metaImageUrl: urls ? (urls.size_1024 || urls.size_2048) : undefined,
      photo: {
        ...coverPhoto.photo,
        images,
      },
    };
  }
  // PortfolioCoverImage variant: the gateway passes the portfolios-api order
  // through as-is, so sort explicitly — the templates read images[0].
  if (coverPhoto.images) {
    const images = [...coverPhoto.images].sort((a, b) => (b?.size || 0) - (a?.size || 0));
    return {
      ...coverPhoto,
      images,
      metaImageUrl: pickMetaImageUrl(images),
    };
  }
  // PortfolioCoverAvatar keeps the legacy shape
  return coverPhoto;
};

const adaptFolder = (folder, photoSizeUrlKeys, coverSizeUrlKeys) => {
  if (!folder) return folder;
  const edges = folder.photos?.edges || [];
  return {
    ...folder,
    coverPhoto: adaptPhoto(folder.coverPhoto, coverSizeUrlKeys),
    photos: folder.photos && {
      ...folder.photos,
      edges: edges.map(edge => edge && ({
        ...edge,
        node: adaptPhoto(edge.node, photoSizeUrlKeys),
      })),
      totalCount: edges.length,
    },
  };
};

const adaptPortfolio = (portfolio) => {
  if (!portfolio) return portfolio;
  // Moodie shows photos as grid cards (size_1024 everywhere); Adams / Newton
  // show a full-viewport slideshow on tablet / desktop (size_2048).
  const themeName = portfolio.theme?.name?.toUpperCase();
  const photoSizeUrlKeys = themeName !== 'MOODIE' && isSlideshowViewport()
    ? SLIDESHOW_PHOTO_SIZE_URL_KEYS
    : PHOTO_SIZE_URL_KEYS;
  const coverSizeUrlKeys = themeName === 'ADAMS'
    ? ADAMS_COVER_SIZE_URL_KEYS
    : COVER_SIZE_URL_KEYS;
  return {
    ...portfolio,
    coverPhoto: adaptCoverPhoto(portfolio.coverPhoto),
    folders: (portfolio.folders || [])
      .map(folder => adaptFolder(folder, photoSizeUrlKeys, coverSizeUrlKeys)),
  };
};

export default adaptPortfolio;
